import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleScroll = () => {
      setIsScrolled((Number(window.scrollY) || 0) > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      requestAnimationFrame(() => requestAnimationFrame(() => setMenuVisible(true)));
    } else {
      setMenuVisible(false);
    }
  }, [mobileMenuOpen]);

  const menuItems = [
    { label: 'About', href: '/', anchor: 'about' },
    { label: 'Repertoire', href: '/', anchor: 'repertoire' },
    { label: 'Videos', href: '/', anchor: 'videos' },
    { label: 'Résumé', href: '/resume', anchor: null },
    { label: 'Photo Gallery', href: '/gallery', anchor: null },
    { label: 'Contact', href: '/', anchor: 'contact' },
  ];

  const handleNavClick = (e: React.MouseEvent, item: typeof menuItems[0]) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (item.anchor) {
      sessionStorage.setItem('scrollToSection', item.anchor);
      navigate('/');
    } else {
      navigate(item.href);
    }
  };

  const isActive = (item: typeof menuItems[0]) => item.href === '/resume' && item.anchor === null;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || mobileMenuOpen
          ? 'bg-deep-black/95 backdrop-blur-md shadow-lg'
          : 'bg-deep-black/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 md:gap-3 cursor-pointer">
            <div className="w-14 h-14 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/1fde6b17644309384f7e4bfbfdddec92.png"
                alt="Wei Chen Badge"
                className="w-14 h-14 object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div
              className="text-lg md:text-2xl font-cinzel tracking-widest transition-colors duration-300"
              style={{ color: '#c9a84c', textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
            >
              WEI CHEN
            </div>
          </a>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {menuItems.map((item) => (
              <a
                key={item.label}
                href={item.anchor ? `/#${item.anchor}` : item.href}
                onClick={(e) => handleNavClick(e, item)}
                className={`font-cormorant text-base transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                  isActive(item) ? 'border-b' : ''
                }`}
                style={{
                  color: isActive(item) ? '#c9a84c' : '#ede8d8',
                  borderColor: isActive(item) ? '#c9a84c' : 'transparent',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
            aria-label="Toggle menu"
          >
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300 origin-center"
              style={{
                backgroundColor: '#c9a84c',
                transform: mobileMenuOpen ? 'translateY(8px) rotate(45deg)' : 'none',
              }}
            />
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300"
              style={{
                backgroundColor: '#c9a84c',
                opacity: mobileMenuOpen ? 0 : 1,
                transform: mobileMenuOpen ? 'scaleX(0)' : 'scaleX(1)',
              }}
            />
            <span
              className="block h-0.5 w-6 rounded-full transition-all duration-300 origin-center"
              style={{
                backgroundColor: '#c9a84c',
                transform: mobileMenuOpen ? 'translateY(-8px) rotate(-45deg)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        className="md:hidden overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight: mobileMenuOpen ? '400px' : '0px', opacity: menuVisible ? 1 : 0 }}
      >
        <div className="relative overflow-hidden" style={{
          background: 'linear-gradient(160deg, #0d0b07 0%, #111008 40%, #0a0d0a 100%)',
          borderTop: '1px solid rgba(201,168,76,0.25)',
        }}>
          <div className="absolute top-0 left-6 right-6 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.6), transparent)' }} />

          <div
            className="absolute right-0 top-0 bottom-0 flex items-center justify-end pointer-events-none select-none overflow-hidden"
            style={{ width: '62%' }}
          >
            <span
              style={{
                fontFamily: '"Didot", "Bodoni MT", "Playfair Display", "IM Fell English", "Libre Baskerville", Georgia, serif',
                fontSize: '22rem',
                lineHeight: 0.85,
                userSelect: 'none',
                display: 'block',
                transform: 'translateX(8%) skewX(-6deg) skewY(1.5deg) scaleY(1.12)',
                background: 'linear-gradient(125deg, #1a0e00 0%, #5a3a00 12%, #a07020 24%, #d4a84c 36%, #ffe8a0 46%, #c9891a 56%, #7a5000 68%, #3a2000 78%, #b8943a 88%, #ffe0a0 96%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                opacity: 0.18,
                filter: 'drop-shadow(3px 6px 8px rgba(0,0,0,0.95)) drop-shadow(-2px -3px 4px rgba(255,210,80,0.18)) drop-shadow(6px 12px 18px rgba(0,0,0,0.85)) drop-shadow(1px 2px 2px rgba(180,120,0,0.3))',
                fontWeight: 400,
                fontStyle: 'normal',
              }}
            >
              𝄢
            </span>
          </div>

          <div className="relative z-10 px-6 py-4">
            {menuItems.map((item, index) => {
              const isLast = index === menuItems.length - 1;
              const active = isActive(item);
              const sharedStyle: React.CSSProperties = {
                transitionDelay: menuVisible ? `${index * 55}ms` : '0ms',
                transform: menuVisible ? 'translateX(0)' : 'translateX(-20px)',
                opacity: menuVisible ? 1 : 0,
                transition: 'transform 0.45s cubic-bezier(0.22,1,0.36,1), opacity 0.45s ease',
              };
              return (
                <div key={item.label}>
                  <a
                    href={item.anchor ? `/#${item.anchor}` : item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    className="flex items-center w-full py-3.5 cursor-pointer"
                    style={sharedStyle}
                  >
                    <span
                      className="font-cinzel tracking-widest text-sm"
                      style={{ color: active ? '#c9a84c' : '#e8d4a0', letterSpacing: '0.14em' }}
                    >
                      {item.label}
                    </span>
                  </a>
                  {!isLast && (
                    <div
                      className="w-full"
                      style={{
                        height: '1px',
                        background: 'linear-gradient(to right, rgba(201,168,76,0.25), rgba(201,168,76,0.08), transparent)',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="absolute bottom-0 left-6 right-6 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(201,168,76,0.4), transparent)' }} />
        </div>
      </div>
    </nav>
  );
}
